/**
 * Cross-Platform Transaction Database Service
 *
 * PHASE 2.2: iOS IAP Service Implementation - Database Integration
 *
 * This service provides comprehensive database operations for cross-platform
 * transaction management, supporting Stripe, iOS IAP, and future Android IAP
 * platforms with unified subscription tracking and transaction lifecycle management.
 *
 * Key Features:
 * - Cross-platform transaction storage and retrieval
 * - Unified subscription management across platforms
 * - Transaction state lifecycle tracking
 * - iOS receipt validation logging
 * - Subscription synchronization and conflict resolution
 * - Performance-optimized queries with proper indexing
 *
 * Database Tables:
 * - cross_platform_transactions: Multi-platform transaction records
 * - subscription_platform_mapping: Unified subscription tracking
 * - transaction_state_history: Transaction lifecycle audit trail
 * - ios_receipt_validation_log: iOS receipt validation tracking
 *
 * Integration:
 * - Used by IOSInAppPurchaseService for transaction persistence
 * - Integrated with PaymentRoutingService for unified payment flows
 * - Supports offline-first architecture with sync capabilities
 *
 * @see docs/DATABASE_SCHEMA_PHASE_2_1.md for complete database documentation
 * @see src/types/crossPlatformTransactions.ts for type definitions
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  CrossPlatformTransaction,
  SubscriptionPlatformMapping,
  TransactionStateHistory,
  IOSReceiptValidationLog,
  TransactionPlatform,
  TransactionStatus,
  SubscriptionStatus,
  SubscriptionInterval,
  TransactionType
} from '@/types/crossPlatformTransactions';
import type { Database } from '@/integrations/supabase/types';

/**
 * Request interface for creating cross-platform transactions
 */
export interface CreateTransactionRequest {
  readonly platform: TransactionPlatform;
  readonly transactionType: TransactionType;
  readonly platformTransactionId: string;
  readonly platformProductId: string;
  readonly unifiedProductId: string;
  readonly subscriptionInterval?: SubscriptionInterval;
  readonly amountCents?: number;
  readonly currency?: string;
  readonly status: TransactionStatus;
  readonly iosReceipt?: string;
  readonly iosReceiptData?: Record<string, unknown>;
  readonly iosTransactionState?: string;
  readonly stripePaymentIntentId?: string;
  readonly stripeSubscriptionId?: string;
  readonly stripeCustomerId?: string;
  readonly metadata?: Record<string, unknown>;
  readonly processedAt?: Date;
  readonly expiresAt?: Date;
}

/**
 * Request interface for creating subscription mappings
 */
export interface CreateSubscriptionMappingRequest {
  readonly unifiedSubscriptionId?: string;
  readonly primaryPlatform: TransactionPlatform;
  readonly stripeSubscriptionId?: string;
  readonly iosOriginalTransactionId?: string;
  readonly androidPurchaseToken?: string;
  readonly productId: string;
  readonly subscriptionInterval: SubscriptionInterval;
  readonly status: SubscriptionStatus;
  readonly startedAt: Date;
  readonly currentPeriodStart?: Date;
  readonly currentPeriodEnd?: Date;
  readonly cancelledAt?: Date;
  readonly expiresAt?: Date;
  readonly syncSource: 'stripe_webhook' | 'ios_receipt' | 'manual' | 'system';
  readonly metadata?: Record<string, unknown>;
}

/**
 * Request interface for logging transaction state changes
 */
export interface LogTransactionStateRequest {
  readonly transactionId: string;
  readonly previousState?: string;
  readonly newState: string;
  readonly stateReason?: string;
  readonly triggeredBy?: 'user' | 'system' | 'webhook' | 'receipt_validation' | 'manual';
  readonly platformEventData?: Record<string, unknown>;
  readonly errorCode?: string;
  readonly errorMessage?: string;
  readonly retryCount?: number;
}

/**
 * Request interface for iOS receipt validation logging
 */
export interface LogIOSReceiptValidationRequest {
  readonly userId?: string;
  readonly transactionId?: string;
  readonly receiptData: string;
  readonly receiptHash: string;
  readonly validationEnvironment?: 'sandbox' | 'production';
  readonly validationStatus: 'success' | 'failed' | 'pending' | 'invalid';
  readonly validationResponse?: Record<string, unknown>;
  readonly errorCode?: number;
  readonly errorDescription?: string;
  readonly retryCount?: number;
}

/**
 * Cross-Platform Transaction Database Service
 * Provides comprehensive database operations for multi-platform transaction management
 */
export class CrossPlatformTransactionService {


  /**
   * Create a new cross-platform transaction record
   */
  async createTransaction(
    userId: string,
    request: CreateTransactionRequest
  ): Promise<CrossPlatformTransaction> {
    console.log('💾 Cross-Platform Transaction Service: Creating transaction:', {
      userId,
      platform: request.platform,
      transactionType: request.transactionType,
      platformTransactionId: request.platformTransactionId,
      unifiedProductId: request.unifiedProductId
    });

    const { data, error } = await supabase
      .from('cross_platform_transactions')
      .insert({
        user_id: userId,
        platform: request.platform,
        transaction_type: request.transactionType,
        platform_transaction_id: request.platformTransactionId,
        platform_product_id: request.platformProductId,
        unified_product_id: request.unifiedProductId,
        subscription_interval: request.subscriptionInterval,
        amount_cents: request.amountCents,
        currency: request.currency || 'USD',
        status: request.status,
        ios_receipt: request.iosReceipt,
        ios_receipt_data: request.iosReceiptData,
        ios_transaction_state: request.iosTransactionState,
        stripe_payment_intent_id: request.stripePaymentIntentId,
        stripe_subscription_id: request.stripeSubscriptionId,
        stripe_customer_id: request.stripeCustomerId,
        metadata: request.metadata || {},
        processed_at: request.processedAt?.toISOString(),
        expires_at: request.expiresAt?.toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to create transaction:', error);
      throw new Error(`Failed to create transaction: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: Transaction created successfully:', data.id);
    return this.mapDatabaseRowToTransaction(data);
  }

  /**
   * Update an existing transaction
   */
  async updateTransaction(
    transactionId: string,
    updates: Partial<CreateTransactionRequest>
  ): Promise<CrossPlatformTransaction> {
    console.log('💾 Cross-Platform Transaction Service: Updating transaction:', {
      transactionId,
      updates: Object.keys(updates)
    });

    const updateData: Record<string, unknown> = {};
    
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.iosReceiptData !== undefined) updateData.ios_receipt_data = updates.iosReceiptData;
    if (updates.iosTransactionState !== undefined) updateData.ios_transaction_state = updates.iosTransactionState;
    if (updates.metadata !== undefined) updateData.metadata = updates.metadata;
    if (updates.processedAt !== undefined) updateData.processed_at = updates.processedAt.toISOString();
    if (updates.expiresAt !== undefined) updateData.expires_at = updates.expiresAt.toISOString();

    const { data, error } = await supabase
      .from('cross_platform_transactions')
      .update(updateData)
      .eq('id', transactionId)
      .select()
      .single();

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to update transaction:', error);
      throw new Error(`Failed to update transaction: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: Transaction updated successfully');
    return this.mapDatabaseRowToTransaction(data);
  }

  /**
   * Get transaction by platform transaction ID
   */
  async getTransactionByPlatformId(
    platform: TransactionPlatform,
    platformTransactionId: string
  ): Promise<CrossPlatformTransaction | null> {
    console.log('🔍 Cross-Platform Transaction Service: Getting transaction by platform ID:', {
      platform,
      platformTransactionId
    });

    const { data, error } = await supabase
      .from('cross_platform_transactions')
      .select()
      .eq('platform', platform)
      .eq('platform_transaction_id', platformTransactionId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        console.log('📭 Cross-Platform Transaction Service: No transaction found');
        return null;
      }
      console.error('❌ Cross-Platform Transaction Service: Failed to get transaction:', error);
      throw new Error(`Failed to get transaction: ${error.message}`);
    }

    return this.mapDatabaseRowToTransaction(data);
  }

  /**
   * Get user transactions with pagination
   */
  async getUserTransactions(
    userId: string,
    options: {
      platform?: TransactionPlatform;
      status?: TransactionStatus;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<readonly CrossPlatformTransaction[]> {
    console.log('🔍 Cross-Platform Transaction Service: Getting user transactions:', {
      userId,
      options
    });

    let query = supabase
      .from('cross_platform_transactions')
      .select()
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options.platform) {
      query = query.eq('platform', options.platform);
    }

    if (options.status) {
      query = query.eq('status', options.status);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to get user transactions:', error);
      throw new Error(`Failed to get user transactions: ${error.message}`);
    }

    console.log(`✅ Cross-Platform Transaction Service: Retrieved ${data.length} transactions`);
    return data.map((row: Database['public']['Tables']['cross_platform_transactions']['Row']) => this.mapDatabaseRowToTransaction(row));
  }

  /**
   * Map database row to CrossPlatformTransaction interface
   */
  private mapDatabaseRowToTransaction(
    row: Database['public']['Tables']['cross_platform_transactions']['Row']
  ): CrossPlatformTransaction {
    return {
      id: row.id,
      user_id: row.user_id,
      platform: row.platform as TransactionPlatform,
      transaction_type: row.transaction_type as TransactionType,
      platform_transaction_id: row.platform_transaction_id,
      platform_product_id: row.platform_product_id,
      unified_product_id: row.unified_product_id,
      subscription_interval: row.subscription_interval as SubscriptionInterval | undefined,
      amount_cents: row.amount_cents ?? undefined,
      currency: row.currency || 'USD',
      status: row.status as TransactionStatus,
      ios_receipt: row.ios_receipt ?? undefined,
      ios_receipt_data: row.ios_receipt_data as Record<string, unknown> | undefined,
      ios_transaction_state: row.ios_transaction_state ?? undefined,
      stripe_payment_intent_id: row.stripe_payment_intent_id ?? undefined,
      stripe_subscription_id: row.stripe_subscription_id ?? undefined,
      stripe_customer_id: row.stripe_customer_id ?? undefined,
      metadata: (row.metadata as Record<string, unknown>) || {},
      processed_at: row.processed_at ?? undefined,
      expires_at: row.expires_at ?? undefined,
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString()
    };
  }

  /**
   * Create or update subscription platform mapping
   */
  async createOrUpdateSubscriptionMapping(
    userId: string,
    request: CreateSubscriptionMappingRequest
  ): Promise<SubscriptionPlatformMapping> {
    console.log('💾 Cross-Platform Transaction Service: Creating/updating subscription mapping:', {
      userId,
      primaryPlatform: request.primaryPlatform,
      productId: request.productId,
      status: request.status
    });

    const mappingData = {
      user_id: userId,
      unified_subscription_id: request.unifiedSubscriptionId || crypto.randomUUID(),
      primary_platform: request.primaryPlatform,
      stripe_subscription_id: request.stripeSubscriptionId,
      ios_original_transaction_id: request.iosOriginalTransactionId,
      android_purchase_token: request.androidPurchaseToken,
      product_id: request.productId,
      subscription_interval: request.subscriptionInterval,
      status: request.status,
      started_at: request.startedAt.toISOString(),
      current_period_start: request.currentPeriodStart?.toISOString(),
      current_period_end: request.currentPeriodEnd?.toISOString(),
      cancelled_at: request.cancelledAt?.toISOString(),
      expires_at: request.expiresAt?.toISOString(),
      sync_source: request.syncSource,
      metadata: request.metadata || {}
    };

    // CRITICAL FIX: Check for existing subscription first, then insert or update
    console.log('💾 Cross-Platform Transaction Service: Checking for existing subscription mapping');

    // First, check if user already has an active subscription
    const existingSubscription = await this.getActiveSubscription(userId);

    let data, error;

    // CRITICAL FIX: Use consistent client for subscription mappings
    // The issue is likely RLS policy mismatch between creation and query contexts
    console.log('💾 Cross-Platform Transaction Service: Using user client for subscription mapping operations');

    if (existingSubscription) {
      console.log('💾 Cross-Platform Transaction Service: Updating existing subscription mapping');
      // Update existing subscription
      const result = await supabase
        .from('subscription_platform_mapping')
        .update({
          primary_platform: mappingData.primary_platform,
          ios_original_transaction_id: mappingData.ios_original_transaction_id,
          stripe_subscription_id: mappingData.stripe_subscription_id,
          android_purchase_token: mappingData.android_purchase_token,
          product_id: mappingData.product_id,
          subscription_interval: mappingData.subscription_interval,
          status: mappingData.status,
          current_period_start: mappingData.current_period_start,
          current_period_end: mappingData.current_period_end,
          sync_source: mappingData.sync_source,
          metadata: mappingData.metadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSubscription.id)
        .select()
        .single();

      data = result.data;
      error = result.error;
    } else {
      console.log('💾 Cross-Platform Transaction Service: Creating new subscription mapping');
      // Create new subscription
      const result = await supabase
        .from('subscription_platform_mapping')
        .insert(mappingData)
        .select()
        .single();

      data = result.data;
      error = result.error;
    }

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to create/update subscription mapping:', error);
      throw new Error(`Failed to create/update subscription mapping: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: Subscription mapping created/updated successfully');

    // DIAGNOSTIC: Log the created mapping details for debugging
    console.log('🔍 Cross-Platform Transaction Service: Created mapping details:', {
      id: data.id,
      user_id: data.user_id,
      product_id: data.product_id,
      status: data.status,
      primary_platform: data.primary_platform,
      ios_original_transaction_id: data.ios_original_transaction_id,
      created_at: data.created_at
    });

    return this.mapDatabaseRowToSubscriptionMapping(data);
  }

  /**
   * Get active subscription for user
   */
  async getActiveSubscription(userId: string): Promise<SubscriptionPlatformMapping | null> {
    console.log('🔍 Cross-Platform Transaction Service: Getting active subscription for user:', userId);

    // DIAGNOSTIC: Log query details for debugging
    console.log('🔍 Cross-Platform Transaction Service: Query details:', {
      table: 'subscription_platform_mapping',
      filters: { user_id: userId, status: 'active' },
      orderBy: 'created_at DESC',
      limit: 1
    });

    // Use consistent user client for subscription mapping queries
    const { data, error } = await supabase
      .from('subscription_platform_mapping')
      .select()
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('📭 Cross-Platform Transaction Service: No active subscription found');
        return null;
      }
      console.error('❌ Cross-Platform Transaction Service: Failed to get active subscription:', error);
      throw new Error(`Failed to get active subscription: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: Found active subscription:', {
      id: data.id,
      userId: data.user_id,
      productId: data.product_id,
      status: data.status,
      platform: data.primary_platform,
      interval: data.subscription_interval
    });

    return this.mapDatabaseRowToSubscriptionMapping(data);
  }

  /**
   * Log transaction state change
   */
  async logTransactionState(request: LogTransactionStateRequest): Promise<TransactionStateHistory> {
    console.log('💾 Cross-Platform Transaction Service: Logging transaction state change:', {
      transactionId: request.transactionId,
      previousState: request.previousState,
      newState: request.newState,
      triggeredBy: request.triggeredBy
    });

    const { data, error } = await supabase
      .from('transaction_state_history')
      .insert({
        transaction_id: request.transactionId,
        previous_state: request.previousState,
        new_state: request.newState,
        state_reason: request.stateReason,
        triggered_by: request.triggeredBy,
        platform_event_data: request.platformEventData,
        error_code: request.errorCode,
        error_message: request.errorMessage,
        retry_count: request.retryCount || 0
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to log transaction state:', error);
      throw new Error(`Failed to log transaction state: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: Transaction state logged successfully');
    return this.mapDatabaseRowToTransactionStateHistory(data);
  }

  /**
   * Log iOS receipt validation attempt
   */
  async logIOSReceiptValidation(request: LogIOSReceiptValidationRequest): Promise<IOSReceiptValidationLog> {
    console.log('💾 Cross-Platform Transaction Service: Logging iOS receipt validation:', {
      userId: request.userId,
      transactionId: request.transactionId,
      validationStatus: request.validationStatus,
      validationEnvironment: request.validationEnvironment
    });

    const { data, error } = await supabase
      .from('ios_receipt_validation_log')
      .insert({
        user_id: request.userId,
        transaction_id: request.transactionId,
        receipt_data: request.receiptData,
        receipt_hash: request.receiptHash,
        validation_environment: request.validationEnvironment,
        validation_status: request.validationStatus,
        validation_response: request.validationResponse,
        error_code: request.errorCode,
        error_description: request.errorDescription,
        retry_count: request.retryCount || 0
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to log iOS receipt validation:', error);
      throw new Error(`Failed to log iOS receipt validation: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: iOS receipt validation logged successfully');
    return this.mapDatabaseRowToIOSReceiptValidationLog(data);
  }

  /**
   * Map database row to SubscriptionPlatformMapping interface
   */
  private mapDatabaseRowToSubscriptionMapping(
    row: Database['public']['Tables']['subscription_platform_mapping']['Row']
  ): SubscriptionPlatformMapping {
    return {
      id: row.id,
      user_id: row.user_id,
      unified_subscription_id: row.unified_subscription_id,
      primary_platform: row.primary_platform as TransactionPlatform,
      stripe_subscription_id: row.stripe_subscription_id ?? undefined,
      ios_original_transaction_id: row.ios_original_transaction_id ?? undefined,
      android_purchase_token: row.android_purchase_token ?? undefined,
      product_id: row.product_id,
      subscription_interval: row.subscription_interval as SubscriptionInterval,
      status: row.status as SubscriptionStatus,
      started_at: row.started_at,
      current_period_start: row.current_period_start ?? undefined,
      current_period_end: row.current_period_end ?? undefined,
      cancelled_at: row.cancelled_at ?? undefined,
      expires_at: row.expires_at ?? undefined,
      last_synced_at: row.last_synced_at || new Date().toISOString(),
      sync_source: row.sync_source as 'stripe_webhook' | 'ios_receipt' | 'manual' | 'system',
      metadata: (row.metadata as Record<string, unknown>) || {},
      created_at: row.created_at || new Date().toISOString(),
      updated_at: row.updated_at || new Date().toISOString()
    };
  }

  /**
   * Map database row to TransactionStateHistory interface
   */
  private mapDatabaseRowToTransactionStateHistory(
    row: Database['public']['Tables']['transaction_state_history']['Row']
  ): TransactionStateHistory {
    return {
      id: row.id,
      transaction_id: row.transaction_id,
      previous_state: row.previous_state ?? undefined,
      new_state: row.new_state,
      state_reason: row.state_reason ?? undefined,
      triggered_by: row.triggered_by as 'user' | 'system' | 'webhook' | 'receipt_validation' | 'manual' | undefined,
      platform_event_data: (row.platform_event_data as Record<string, unknown>) || undefined,
      error_code: row.error_code ?? undefined,
      error_message: row.error_message ?? undefined,
      retry_count: row.retry_count || 0,
      created_at: row.created_at || new Date().toISOString()
    };
  }

  /**
   * Get iOS receipt validation logs for a user
   */
  async getIOSReceiptValidationLogs(
    userId: string,
    options: {
      receiptHash?: string;
      transactionId?: string;
      validationStatus?: string;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<readonly IOSReceiptValidationLog[]> {
    console.log('🔍 Cross-Platform Transaction Service: Getting iOS receipt validation logs:', {
      userId,
      options
    });

    let query = supabase
      .from('ios_receipt_validation_log')
      .select()
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (options.receiptHash) {
      query = query.eq('receipt_hash', options.receiptHash);
    }

    if (options.transactionId) {
      query = query.eq('transaction_id', options.transactionId);
    }

    if (options.validationStatus) {
      query = query.eq('validation_status', options.validationStatus);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.range(options.offset, (options.offset + (options.limit || 10)) - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to get iOS receipt validation logs:', error);
      throw new Error(`Failed to get iOS receipt validation logs: ${error.message}`);
    }

    console.log(`✅ Cross-Platform Transaction Service: Retrieved ${data.length} iOS receipt validation logs`);
    return data.map(this.mapDatabaseRowToIOSReceiptValidationLog);
  }

  /**
   * Update iOS receipt validation log
   */
  async updateIOSReceiptValidationLog(
    logId: string,
    updates: {
      validationStatus?: string;
      validationEnvironment?: string;
      validationResponse?: string;
      errorCode?: string;
      errorDescription?: string;
    }
  ): Promise<IOSReceiptValidationLog> {
    console.log('🔄 Cross-Platform Transaction Service: Updating iOS receipt validation log:', {
      logId,
      updates
    });

    const { data, error } = await supabase
      .from('ios_receipt_validation_log')
      .update({
        validation_status: updates.validationStatus,
        validation_environment: updates.validationEnvironment,
        validation_response: updates.validationResponse ? JSON.parse(updates.validationResponse) : undefined,
        error_code: updates.errorCode,
        error_description: updates.errorDescription,
        updated_at: new Date().toISOString()
      })
      .eq('id', logId)
      .select()
      .single();

    if (error) {
      console.error('❌ Cross-Platform Transaction Service: Failed to update iOS receipt validation log:', error);
      throw new Error(`Failed to update iOS receipt validation log: ${error.message}`);
    }

    console.log('✅ Cross-Platform Transaction Service: iOS receipt validation log updated successfully');
    return this.mapDatabaseRowToIOSReceiptValidationLog(data);
  }

  /**
   * Map database row to IOSReceiptValidationLog interface
   */
  private mapDatabaseRowToIOSReceiptValidationLog(
    row: Database['public']['Tables']['ios_receipt_validation_log']['Row']
  ): IOSReceiptValidationLog {
    return {
      id: row.id,
      user_id: row.user_id ?? undefined,
      transaction_id: row.transaction_id ?? undefined,
      receipt_data: row.receipt_data,
      receipt_hash: row.receipt_hash,
      validation_environment: row.validation_environment as 'sandbox' | 'production' | undefined,
      validation_status: row.validation_status as 'success' | 'failed' | 'pending' | 'invalid',
      validation_response: (row.validation_response as Record<string, unknown>) || undefined,
      error_code: row.error_code ?? undefined,
      error_description: row.error_description ?? undefined,
      retry_count: row.retry_count || 0,
      validated_at: row.validated_at || new Date().toISOString()
    };
  }
}

// Export singleton instance
export const crossPlatformTransactionService = new CrossPlatformTransactionService();
